<template>
  <dashboard-layout title="Feedback Detail" back-to="/thumbs" back-label="Likes & Dislikes">
    <loading-state v-if="loading" label="Loading…" />

    <template v-else-if="thumb && userData">
      <data-panel title="Feedback">
        <div class="type">
          <img v-if="thumb.feedbackThumb === 'up'" src="../assets/up.png" alt="Up" class="thumb-icon">
          <img v-else-if="thumb.feedbackThumb === 'down'" src="../assets/down.png" alt="Down" class="thumb-icon">
          <status-badge :variant="thumb.feedbackThumb === 'up' ? 'success' : 'danger'">
            {{ thumb.feedbackThumb === 'up' ? 'Up' : 'Down' }}
          </status-badge>
        </div>
        <div class="meta">
          <div class="item">
            <span class="label">Date</span>
            <span class="value">{{ formatTimestamp(thumb.createdAt) }}</span>
          </div>
          <div class="item">
            <span class="label">User ID</span>
            <router-link :to="`/user/${thumb.userId}`" class="user-link">{{ thumb.userId }}</router-link>
          </div>
        </div>
      </data-panel>

      <data-panel title="Message">
        <div class="msg">{{ thumb.content }}</div>
      </data-panel>

      <data-panel title="User">
        <div class="user-block">
          <div class="avatar">{{ getUserInitials(userData.properties?.name) }}</div>
          <div class="details">
            <div class="name">{{ userData.properties?.name || 'Unknown' }}</div>
            <router-link :to="`/user/${thumb.userId}`" class="profile-link">View Profile</router-link>
          </div>
        </div>
      </data-panel>
    </template>

    <empty-state v-else icon="thumbs" title="Feedback not found">
      This feedback entry could not be loaded.
      <router-link to="/thumbs" class="empty-back">Back to Likes &amp; Dislikes</router-link>
    </empty-state>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseThumbService } from '../services/firebaseThumbService'
import { FirebaseUserService } from '../services/firebaseUserService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'ThumbDetailView',
  components: {
    DashboardLayout, DataPanel, StatusBadge, LoadingState, EmptyState
  },
  setup() {
    const route = useRoute()
    const service = new FirebaseThumbService()
    const userService = new FirebaseUserService()
    const thumb = ref(null)
    const userData = ref(null)
    const loading = ref(true)

    const loadData = async () => {
      try {
        const id = route.params.id
        const data = await service.getThumbById(id)
        thumb.value = data
        if (data && data.userId) {
          userData.value = await userService.getUserById(data.userId)
        }
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const formatTimestamp = ts => {
      if (!ts) return '-'
      if (ts.seconds) {
        const d = new Date(ts.seconds * 1000)
        return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      }
      const d = new Date(ts)
      return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const getUserInitials = name => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    onMounted(() => loadData())

    return { thumb, userData, loading, formatTimestamp, getUserInitials }
  }
}
</script>

<style scoped>
.type {
  display: flex;
  align-items: center;
  gap: 12px;
}
.thumb-icon {
  width: 32px;
  height: 32px;
}
.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}
.item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  border-radius: 12px;
  min-width: 200px;
}
.label {
  font-size: 12px;
  color: var(--text-secondary);
}
.value {
  font-size: 14px;
  color: var(--text-primary);
}
.user-link {
  color: var(--primary-color);
  text-decoration: none;
}
.user-link:hover {
  text-decoration: underline;
}
.msg {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 12px;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
}
.user-block {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 12px;
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
}
.details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.name {
  font-weight: 600;
  color: var(--text-primary);
}
.profile-link {
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
}
.profile-link:hover {
  text-decoration: underline;
}
.empty-back {
  display: inline-block;
  margin-top: 4px;
  color: var(--primary-color);
  text-decoration: none;
}
.empty-back:hover {
  text-decoration: underline;
}

@media (max-width: 1024px) {
  .item {
    min-width: 100%;
  }
}
</style>
